import { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet, apiPost, apiDelete } from '../services/api';
import { formatDuration, secondsToHours } from '../utils/time';
import TimeEntryModal from '../components/TimeEntryModal';
import {
  Play,
  Square,
  Pause,
  RotateCcw,
  Plus,
  Trash2,
  Edit,
  DollarSign,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface Project {
  id: string;
  name: string;
  color: string | null;
  hourly_rate: number | null;
}

interface TimeEntry {
  id: string;
  project_id: string | null;
  project_name: string | null;
  project_color: string | null;
  description: string | null;
  start_time: string;
  end_time: string | null;
  duration: number | null;
  is_billable: boolean;
  hourly_rate: number | null;
  is_paused: boolean;
  paused_at: string | null;
  total_paused_duration: number;
}

interface PaginatedResponse {
  items: TimeEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export default function TimeTrack() {
  // Timer state
  const [runningEntry, setRunningEntry] = useState<TimeEntry | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<number | null>(null);

  // Timer form
  const [description, setDescription] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [isBillable, setIsBillable] = useState(true);

  // Projects list
  const [projects, setProjects] = useState<Project[]>([]);

  // Entries list
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Modal
  const [modalEntry, setModalEntry] = useState<TimeEntry | null | undefined>(undefined);

  // Load projects and check for running timer on mount
  useEffect(() => {
    fetchProjects();
    checkRunningTimer();
  }, []);

  // Load entries when page or filters change
  useEffect(() => {
    fetchEntries();
  }, [page, startDate, endDate]);

  // Timer tick
  useEffect(() => {
    if (runningEntry && !runningEntry.is_paused) {
      intervalRef.current = window.setInterval(() => {
        const start = new Date(runningEntry.start_time).getTime();
        const now = Date.now();
        const totalPaused = (runningEntry.total_paused_duration || 0) * 1000;
        setElapsed(Math.floor((now - start - totalPaused) / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // If paused, compute frozen elapsed
      if (runningEntry && runningEntry.is_paused && runningEntry.paused_at) {
        const start = new Date(runningEntry.start_time).getTime();
        const pausedAt = new Date((runningEntry as any).paused_at).getTime();
        const totalPaused = (runningEntry.total_paused_duration || 0) * 1000;
        setElapsed(Math.floor((pausedAt - start - totalPaused) / 1000));
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [runningEntry]);

  const fetchProjects = async () => {
    try {
      const data = await apiGet<{ items: Project[] }>('/api/projects?page_size=100&status=active');
      setProjects(data.items);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const checkRunningTimer = async () => {
    try {
      const entry = await apiGet<TimeEntry | null>('/api/time-entries/running');
      if (entry) {
        setRunningEntry(entry);
        setDescription(entry.description || '');
        setSelectedProjectId(entry.project_id || '');
        setIsBillable(entry.is_billable);
      }
    } catch (err) {
      console.error('Failed to check running timer:', err);
    }
  };

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '20');
      if (startDate) params.set('start_date', new Date(startDate).toISOString());
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.set('end_date', end.toISOString());
      }

      const data = await apiGet<PaginatedResponse>(`/api/time-entries?${params}`);
      setEntries(data.items);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page, startDate, endDate]);

  const handleStart = async () => {
    try {
      const entry = await apiPost<TimeEntry>('/api/time-entries/start', {
        project_id: selectedProjectId || null,
        description: description || null,
        is_billable: isBillable,
      });
      setRunningEntry(entry);
    } catch (err: any) {
      alert(err.message || 'Failed to start timer');
    }
  };

  const handleStop = async () => {
    if (!runningEntry) return;
    try {
      await apiPost(`/api/time-entries/${runningEntry.id}/stop`);
      setRunningEntry(null);
      setElapsed(0);
      setDescription('');
      setSelectedProjectId('');
      fetchEntries();
    } catch (err: any) {
      alert(err.message || 'Failed to stop timer');
    }
  };

  const handlePause = async () => {
    if (!runningEntry) return;
    try {
      const updated = await apiPost<TimeEntry>(`/api/time-entries/${runningEntry.id}/pause`);
      setRunningEntry(updated);
    } catch (err: any) {
      alert(err.message || 'Failed to pause timer');
    }
  };

  const handleResume = async () => {
    if (!runningEntry) return;
    try {
      const updated = await apiPost<TimeEntry>(`/api/time-entries/${runningEntry.id}/resume`);
      setRunningEntry(updated);
    } catch (err: any) {
      alert(err.message || 'Failed to resume timer');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (!confirm('Delete this time entry?')) return;
    try {
      await apiDelete(`/api/time-entries/${entryId}`);
      fetchEntries();
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  };

  const formatTime = (iso: string) => {
    return new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">TimeTrack</h1>
        <p className="text-gray-400">Track your time and manage entries</p>
      </div>

      {/* Timer Section */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Description input */}
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What are you working on?"
            disabled={!!runningEntry}
            className="flex-1 px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />

          {/* Project dropdown */}
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            disabled={!!runningEntry}
            className="px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 min-w-[200px]"
          >
            <option value="">No project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {/* Billable toggle */}
          <button
            onClick={() => !runningEntry && setIsBillable(!isBillable)}
            disabled={!!runningEntry}
            className={`px-4 py-3 rounded-lg border transition-colors flex items-center gap-2 disabled:opacity-50 ${
              isBillable
                ? 'bg-green-500/10 border-green-500/30 text-green-400'
                : 'bg-dark-800 border-dark-700 text-gray-500'
            }`}
            title={isBillable ? 'Billable' : 'Non-billable'}
          >
            <DollarSign className="w-5 h-5" />
          </button>

          {/* Timer display */}
          <div className="flex items-center gap-3">
            <span className={`text-2xl font-mono font-bold min-w-[110px] text-center ${
              runningEntry ? (runningEntry.is_paused ? 'text-yellow-400' : 'text-green-400') : 'text-gray-500'
            }`}>
              {formatDuration(elapsed)}
            </span>

            {/* Timer controls */}
            {!runningEntry ? (
              <button
                onClick={handleStart}
                className="p-3 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                title="Start timer"
              >
                <Play className="w-5 h-5" />
              </button>
            ) : (
              <div className="flex gap-2">
                {runningEntry.is_paused ? (
                  <button
                    onClick={handleResume}
                    className="p-3 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                    title="Resume"
                  >
                    <RotateCcw className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={handlePause}
                    className="p-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full transition-colors"
                    title="Pause"
                  >
                    <Pause className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={handleStop}
                  className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                  title="Stop"
                >
                  <Square className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Entries Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-white">Time Entries ({total})</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="px-3 py-1.5 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="px-3 py-1.5 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={() => setModalEntry(null)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Manual Entry
          </button>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-800">
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Project</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Description</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Time</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Duration</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-400">Billable</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No time entries found</td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="border-b border-dark-800/50 hover:bg-dark-800/30">
                    <td className="px-4 py-3">
                      {entry.project_name ? (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: entry.project_color || '#6366f1' }}
                          />
                          <span className="text-sm text-white">{entry.project_name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No project</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-300">{entry.description || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-400">{formatDate(entry.start_time)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-400">
                        {formatTime(entry.start_time)}
                        {entry.end_time ? ` - ${formatTime(entry.end_time)}` : ' (running)'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-mono text-white">
                        {entry.duration ? formatDuration(entry.duration) : '--:--:--'}
                      </span>
                      {entry.duration && (
                        <span className="text-xs text-gray-500 ml-1">
                          ({secondsToHours(entry.duration)}h)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {entry.is_billable ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-500/10 text-green-400">
                          <DollarSign className="w-3 h-3 mr-0.5" />Yes
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setModalEntry(entry)}
                          className="p-1.5 hover:bg-dark-700 rounded text-gray-400 hover:text-white"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="p-1.5 hover:bg-dark-700 rounded text-gray-400 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalEntry !== undefined && (
        <TimeEntryModal
          entry={modalEntry}
          onClose={() => setModalEntry(undefined)}
          onSaved={() => {
            setModalEntry(undefined);
            fetchEntries();
          }}
        />
      )}
    </div>
  );
}
